import { Icon, type IconName } from '@capsico/ui';
import { Toggle } from './ui';

/*
 * W18 notification blocks (capsico-customer-app-ui): the bell with its unread badge, inbox rows
 * and preference rows. Presentation only — strings arrive translated, data lives in hooks.
 */

/** Bell for the Home / Account headers: 36px tile, yellow count badge with ink text. */
export function BellButton({
  label,
  count,
  onClick,
}: {
  label: string;
  count: number | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-tile text-ink active:scale-95"
    >
      <Icon name="bell" size={18} />
      {count !== null && count > 0 && (
        <span className="absolute -top-1 -right-1 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1 text-micro font-bold text-on-brand tabular shadow-[0_0_0_2px_var(--color-surface)]">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

/** Fixed row height so long inboxes can be virtualised. */
export const INBOX_ROW_HEIGHT = 96;

/** One message: category icon tile, title, 2-line body, time; unread = yellow dot + bold title. */
export function InboxRow({
  icon,
  category,
  title,
  body,
  when,
  unread,
  unreadLabel,
  onOpen,
}: {
  icon: IconName;
  category: string;
  title: string;
  body: string;
  when: string;
  unread: boolean;
  unreadLabel: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex h-24 w-full items-start gap-3 px-4 py-3 text-left shadow-[inset_0_-1px_0_var(--color-hair)] active:bg-tile ${unread ? 'bg-brand-soft/40' : 'bg-surface'}`}
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name={icon} size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm text-ink ${unread ? 'font-bold' : 'font-medium'}`}
          >
            {title}
          </span>
          {unread && (
            <span
              role="img"
              aria-label={unreadLabel}
              className="size-2 shrink-0 rounded-full bg-brand"
            />
          )}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-xs leading-4 text-ink-2">{body}</span>
        <span className="mt-1 block truncate text-caption text-ink-3">
          {category} · {when}
        </span>
      </span>
    </button>
  );
}

/** Preference row: label + hint left, switch right. Locked rows show why under the hint. */
export function PrefRow({
  title,
  hint,
  on,
  onChange,
  disabled = false,
  lockedNote,
}: {
  title: string;
  hint?: string;
  on: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  lockedNote?: string;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {hint && <p className="mt-0.5 text-xs text-ink-2">{hint}</p>}
        {lockedNote && (
          <p className="mt-1 flex items-center gap-1 text-caption text-ink-3">
            <Icon name="lock" size={12} />
            {lockedNote}
          </p>
        )}
      </div>
      <Toggle on={on} onChange={onChange} label={title} disabled={disabled} />
    </div>
  );
}
